import * as React from 'react'
import { createContext, useState } from 'react'
import { AddressType } from '@quikplow/quikplow-types'
// eslint-disable-next-line
// @ts-ignores
import { unflatten } from 'flatley'

import { post } from '@root/api'
import { APP_ROUTES } from '@root/appRoutes'
import {
	isValidDrivewayLengthType,
	isValidDrivewayRelocationDistanceType,
	isValidDrivewayShapeType,
	isValidDrivewaySurfaceType,
	isValidDrivewayWidthType,
	isValidPropertyType,
} from '@root/types/helperFunctions'
import { useAppConfig } from '../AppConfig'

const defaultState = {
	line1: undefined,
	city: undefined,
	state: undefined,
	zip: undefined,
	type: undefined,
	drivewayDetails: {
		surface: undefined,
		shape: undefined,
		width: undefined,
		length: undefined,
		relocationDistance: undefined,
	},
}

type State = any
type ActionTypes = 'UPDATE_NEW_ADDRESS' | 'RESET_NEW_ADDRESS'
type Action = {
	type: ActionTypes
	payload?: {
		[key: string]: unknown
	}
}
const reducer = (state: State, action: Action): State => {
	let reducedState = { ...state }
	const { type, payload } = action
	const unflattenedPayload = unflatten(payload)

	switch (type) {
		case 'UPDATE_NEW_ADDRESS':
			if (unflattenedPayload) {
				reducedState = {
					...reducedState,
					...unflattenedPayload,
				}
			}
			break

		case 'RESET_NEW_ADDRESS':
			reducedState = defaultState
			break

		default:
			break
	}

	return reducedState
}

type NewAddressBasicsDetailsType = Pick<AddressType, 'line1' | 'line2' | 'city' | 'state' | 'zip'>
type ContextType = {
	newAddressInfo: AddressType
	dispatchNewAddressInfo: ({ type, payload }: Action) => void
	isServiceableAddress: (serviceableAddress: NewAddressBasicsDetailsType) => Promise<boolean>
	returnRoute: string
	setReturnRoute: (redirectTo: string) => void
	currentStep: number
	setCurrentStep: (stepNumber: number) => void
	stepBack: () => void
	stepForward: () => void
	validatedSteps: {
		step1: boolean
		step2: boolean
	}
}

const Context = createContext<ContextType>({} as ContextType)

type NewAddressProviderProps = {
	maxSteps?: number
	children: React.ReactNode
}
const NewAddressProvider: React.FC<NewAddressProviderProps> = ({ children, maxSteps = 4 }) => {
	const [newAddressInfo, dispatchNewAddressInfo] = React.useReducer(reducer, defaultState)
	const [returnRoute, setReturnRoute] = useState(APP_ROUTES.authenticated.customers.account)
	const [currentStep, setCurrentStep] = React.useState(0)
	const [validatedSteps, setValidSteps] = React.useState({ step1: false, step2: false })
	const { apiDomain } = useAppConfig()

	const stepBack = React.useCallback(() => {
		setCurrentStep(Math.max(0, currentStep - 1))
	}, [currentStep])

	const stepForward = React.useCallback(() => {
		setCurrentStep(Math.min(maxSteps, currentStep + 1))
	}, [currentStep, maxSteps])

	const isServiceableAddress = React.useCallback(
		async (addressToCheck: NewAddressBasicsDetailsType): Promise<boolean> => {
			let isServiced = false
			const res = await post(`${apiDomain}/api/customer/validate-address`, {
				body: JSON.stringify(addressToCheck),
			})

			if (res.status === 200) {
				;({ isServiced } = await res.json())
			}

			return isServiced
		},
		[apiDomain]
	)

	const validateSteps = React.useCallback(() => {
		const validSteps = {
			step1: false,
			step2: false,
		}
		if (
			newAddressInfo.line1 !== undefined &&
			newAddressInfo.city !== undefined &&
			newAddressInfo.state !== undefined &&
			newAddressInfo.zip !== undefined
		) {
			validSteps.step1 = true
		}

		if (
			validSteps.step1 &&
			newAddressInfo.city !== undefined &&
			newAddressInfo.city !== undefined &&
			isValidPropertyType(newAddressInfo.type) &&
			isValidDrivewaySurfaceType(newAddressInfo.drivewayDetails.surface) &&
			isValidDrivewayShapeType(newAddressInfo.drivewayDetails.shape) &&
			isValidDrivewayWidthType(newAddressInfo.drivewayDetails.width) &&
			isValidDrivewayLengthType(newAddressInfo.drivewayDetails.length) &&
			newAddressInfo.drivewayDetails.relocationDescription !== undefined &&
			isValidDrivewayRelocationDistanceType(newAddressInfo.drivewayDetails.relocationDistance)
		) {
			validSteps.step2 = true
		}

		setValidSteps(validSteps)
	}, [newAddressInfo])

	React.useEffect(() => {
		validateSteps()
	}, [validateSteps])

	return (
		<Context.Provider
			value={{
				newAddressInfo,
				dispatchNewAddressInfo,
				isServiceableAddress,
				returnRoute,
				setReturnRoute,
				currentStep,
				setCurrentStep,
				stepBack,
				stepForward,
				validatedSteps,
			}}
		>
			{children}
		</Context.Provider>
	)
}

const useNewAddress = (): ContextType => React.useContext(Context)

export { NewAddressProvider, useNewAddress }
